import { useState } from "react";
import { useTranslation } from "react-i18next";
import { WorkSecurityArea } from "services/work-security-areas.service";
import { ChangeLink, SettingRow, SettingsCard } from "../../ui";
import AreaEditor from "./AreaEditor";

function Pill({ children }: { children: string }) {
  return (
    <span className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-700">
      {children}
    </span>
  );
}

interface AreaRowProps {
  companyId: string;
  area: WorkSecurityArea;
  onChanged: () => Promise<unknown>;
}

function AreaRow({ companyId, area, onChanged }: AreaRowProps) {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);

  const responsibles = area.responsibles ?? [];
  const summary = [
    area.hasIncidents && t("work_security_area_can_register_incidents"),
    responsibles.length > 0
      ? t("work_security_area_responsibles_list", {
          names: responsibles.map((person) => person.name).join(", "),
        })
      : t("work_security_area_no_responsibles"),
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <SettingRow
      label={area.name}
      description={summary}
      value={
        (area.isSystem || area.isArchived) && (
          <span className="flex flex-wrap gap-1.5">
            {area.isSystem && (
              <Pill>{t("work_security_area_system_badge")}</Pill>
            )}
            {area.isArchived && (
              <Pill>{t("work_security_area_archived_badge")}</Pill>
            )}
          </span>
        )
      }
      action={<ChangeLink onClick={() => setEditing(true)} />}
    >
      {editing && (
        <AreaEditor
          companyId={companyId}
          area={area}
          onChanged={onChanged}
          onClose={() => setEditing(false)}
        />
      )}
    </SettingRow>
  );
}

interface AreasCardProps {
  companyId: string;
  areas: WorkSecurityArea[];
  /** Reloads the list of areas. */
  onChanged: () => Promise<unknown>;
}

/** The instruction areas ("sritys") of the company, each with its own responsible people. */
export default function AreasCard({
  companyId,
  areas,
  onChanged,
}: AreasCardProps) {
  const { t } = useTranslation();
  const [creating, setCreating] = useState(false);

  return (
    <SettingsCard
      title={t("work_security_areas_title")}
      description={t("work_security_areas_hint")}
      actions={
        <button
          type="button"
          onClick={() => setCreating(true)}
          className="inline-flex min-h-11 items-center md:min-h-9 [@media(pointer:coarse)]:min-h-11 rounded-lg border border-gray-300 bg-white px-4 text-sm font-medium text-gray-900 hover:bg-gray-50"
        >
          {t("work_security_area_create")}
        </button>
      }
    >
      {creating && (
        <div className="mb-3.5">
          <AreaEditor
            companyId={companyId}
            onChanged={onChanged}
            onClose={() => setCreating(false)}
          />
        </div>
      )}
      {areas.map((area) => (
        <AreaRow
          key={area.id}
          companyId={companyId}
          area={area}
          onChanged={onChanged}
        />
      ))}
    </SettingsCard>
  );
}
